import React from "react";
import { Link } from "react-router-dom";

/**
 * Official UTAL MEA LLC branding.
 * Renders the logo image with a UTAL MEA-red glow accent.
 * Set `variant="dark"` on dark backgrounds so the logo sits on a subtle white plate
 * that keeps the red UTAL text and dark MEA badge legible.
 */
export function Brand({
    to = "/",
    variant = "light",         // "light" | "dark"
    size = "md",               // "sm" | "md" | "lg"
    tagline = null,            // optional small text under logo
    taglineColor = "text-muted-foreground",
    className = "",
    dataTestId = "brand",
}) {
    const heights = { sm: "h-6", md: "h-8", lg: "h-11" };
    const h = heights[size] || heights.md;
    const isDark = variant === "dark";
    const inner = (
        <div className={`inline-flex items-center gap-3 ${className}`} data-testid={dataTestId}>
            <span
                className={
                    isDark
                        ? "inline-flex items-center px-2 py-1 rounded-sm bg-white/95"
                        : "inline-flex items-center"
                }
            >
                <img
                    src="/utal-mea-logo.png"
                    alt="UTAL MEA LLC"
                    className={`${h} w-auto block select-none`}
                    draggable="false"
                />
            </span>
            {tagline && (
                <span className={`hidden sm:block text-[10px] tracking-[0.25em] uppercase ${taglineColor}`}>{tagline}</span>
            )}
        </div>
    );
    return to ? (
        /^(https?:)?\/\//.test(to) ? (
            <a href={to} target="_blank" rel="noopener noreferrer" className="inline-block" data-testid={`${dataTestId}-link`}>
                {inner}
            </a>
        ) : (
            <Link to={to} className="inline-block" data-testid={`${dataTestId}-link`}>
                {inner}
            </Link>
        )
    ) : (
        inner
    );
}

/**
 * Inline "UTAL MEA" word-mark for tight spots (footers, print headers).
 * "UTAL" is bold red, "MEA" is bold neutral — matches the logo's colour weights.
 */
export function BrandText({ className = "", dataTestId = "brand-text" }) {
    return (
        <span className={`font-black tracking-[0.25em] ${className}`} data-testid={dataTestId}>
            <span className="text-primary">UTAL</span>
            <span className="ms-1">MEA</span>
        </span>
    );
}

export default Brand;
